<script setup>
import Bold from '@tiptap/extension-bold';
import Color from '@tiptap/extension-color';
import FontFamily from '@tiptap/extension-font-family';
import Highlight from '@tiptap/extension-highlight';
import Image from '@tiptap/extension-image';
import Italic from '@tiptap/extension-italic';
import Link from '@tiptap/extension-link';
import TextAlign from '@tiptap/extension-text-align';
import Underline from '@tiptap/extension-underline';
import YouTube from '@tiptap/extension-youtube';
import StarterKit from '@tiptap/starter-kit';
import { Editor, EditorContent } from '@tiptap/vue-3';
import { nextTick, onMounted, ref, watch } from 'vue';

const props = defineProps({
    modelValue: {
        type: String,
        default: ''
    },
    inputId: {
        type: String,
        default: ''
    },
    label: {
        type: String,
        default: ''
    },
    required: {
        type: Boolean,
        default: false
    },
    placeholder: {
        type: String,
        default: ''
    },
    disabled: {
        type: Boolean,
        default: false
    },
    marginTop: {
        type: String,
        default: '0'
    },
    marginBottom: {
        type: String,
        default: '0'
    },
    marginLeft: {
        type: String,
        default: '0'
    },
    marginRight: {
        type: String,
        default: '0'
    },
    labelRightmargin: {
        type: String,
        default: '2rem'
    },
    visible: {
        type: Boolean,
        default: true
    },
    maxlength: {
        type: Number,
        default: 4000
    },
    height: {
        type: String,
        default: '30rem'
    },
    validationCheck: {
        type: Function,
        default: () => ({ isValid: true, message: '' })
    },
    readonly: {
        type: Boolean,
        default: false
    },
    errmsg: {
        type: String,
        default: ''
    },
    labelEmpty: {
        type: Boolean,
        default: false
    },
    borderNone: {
        type: Boolean,
        default: false
    }
});

const emit = defineEmits(['update:modelValue', 'input', 'change']);

const editor = ref(null);
const ValidationMsg = ref('');

onMounted(async () => {
    await nextTick();
    editor.value = new Editor({
        content: props.modelValue,
        extensions: [
            StarterKit.configure({
                history: false
            }),
            Bold,
            Italic,
            Underline,
            Link,
            TextAlign,
            Image,
            FontFamily,
            Color,
            Highlight,
            YouTube
        ],
        editorProps: {
            attributes: {
                class: 'prose prose-sm sm:max-w-none dark:text-white dark:bg-gray-800 bg-white p-4 rounded-md overflow-auto'
            }
        },
        editable: !props.readonly,
        onUpdate: ({ editor }) => {
            emit('update:modelValue', editor.getHTML());
            emit('change', editor.getHTML());
        }
    });
});

watch(
    () => props.modelValue,
    (newValue) => {
        if (editor.value && editor.value.getHTML() !== newValue) {
            editor.value.commands.setContent(newValue, false);
        }
    }
);

const clearContent = () => {
    if (editor.value) {
        editor.value.commands.clearContent();
    }
};
</script>

<template>
    <div
        class="flex flex-col"
        :style="{
            marginTop: marginTop,
            marginBottom: marginBottom,
            marginLeft: marginLeft,
            marginRight: marginRight
        }"
    >
        <div class="relative flex items-center w-full">
            <div
                v-if="visible"
                :id="inputId"
                class="editor-container"
                :class="{
                    'error-border': ValidationMsg,
                    'border-none': borderNone
                }"
                :style="{ height: height }"
            >
                <div class="tiptap-editor">
                    <Toolbar :editor="editor" />
                    <editor-content :editor="editor" class="editor-content" />
                </div>
            </div>
            <div class="absolute right-2 top-1/2 transform -translate-y-1/2 flex items-center">
                <button v-if="editor && editor.getHTML()" @click="clearContent" class="clear-button">&times;</button>
            </div>
        </div>
    </div>
</template>

<style scoped>
.editor-container {
    padding: 0.75rem;
    border: 0.1rem solid #d1d5db;
    border-radius: 0.5rem;
    overflow-y: auto;
}

.editor-content {
    padding: 10px;
    min-height: 100%;
    height: 100%;
    overflow-y: auto;
    outline: none;
}

.ProseMirror {
    min-height: 100%;
    height: 100%;
}

.label {
    font-family: 'SCoreDream500';
    display: flex;
    align-items: center;
    margin-bottom: 0.25rem;
    margin-left: 0.25rem;
    height: 1.3rem;
    font-size: 1rem;
}

.required-asterisk {
    color: #f87171; /* 빨간색 */
    margin-left: 0.2rem;
    position: relative;
    top: -0.2rem;
}

.editor-container:focus {
    border-color: #0038ea;
    outline: none;
}

.tiptap-editor {
    border: 1px solid #ccc;
    border-radius: 4px;
}

.editor-content {
    padding: 10px;
    min-height: 150px;
    outline: none;
}

.error-border {
    border: 1px solid red !important;
}

.none-border {
    border: none;
}

.validation-message {
    color: #f87171;
    font-size: 0.75rem;
    margin-left: 0.5rem;
}

.clear-button {
    cursor: pointer;
    background: transparent;
    border: none;
    font-size: 1.4rem;
    color: #6b7280;
    padding: 0.25rem;
}

.clear-button:hover {
    color: #4b5563;
}
</style>
